"use client";

import { useCallback, useEffect, useId, useState } from "react";
import { infocommPhotos, type InfoCommPhoto } from "@/lib/data/infocomm-photos";

function PhotoImg({
  photo,
  className,
  priority,
}: {
  photo: InfoCommPhoto;
  className?: string;
  priority?: boolean;
}) {
  return (
    // Native img: already compressed JPEGs in /public. next/image was stalling
    // the first load of this 31-photo gallery on localhost.
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={photo.src}
      alt={photo.alt}
      width={photo.width}
      height={photo.height}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      className={className}
    />
  );
}

export default function InfoCommPhotoGallery() {
  const labelId = useId();
  const photos = infocommPhotos;
  const [active, setActive] = useState<number | null>(null);
  const photo = active === null ? null : photos[active];

  const close = useCallback(() => setActive(null), []);
  const showPrev = useCallback(() => {
    setActive(current =>
      current === null ? current : (current + photos.length - 1) % photos.length
    );
  }, [photos.length]);
  const showNext = useCallback(() => {
    setActive(current =>
      current === null ? current : (current + 1) % photos.length
    );
  }, [photos.length]);

  useEffect(() => {
    if (active === null) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowLeft") showPrev();
      if (event.key === "ArrowRight") showNext();
    };

    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [active, close, showPrev, showNext]);

  return (
    <>
      <ul className="columns-1 gap-4 sm:columns-2 lg:columns-3">
        {photos.map((item, index) => (
          <li key={item.src} className="mb-4 break-inside-avoid">
            <button
              type="button"
              onClick={() => setActive(index)}
              className="group block w-full overflow-hidden rounded-lg bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              aria-label={`Open photo ${index + 1} of ${photos.length}`}
            >
              <PhotoImg
                photo={item}
                priority={index < 3}
                className="h-auto w-full transition duration-300 group-hover:scale-[1.02]"
              />
            </button>
          </li>
        ))}
      </ul>

      {photo ? (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelId}
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/85"
            aria-label="Close photo"
            onClick={close}
          />
          <p id={labelId} className="sr-only">
            {photo.alt}
          </p>
          <button
            type="button"
            onClick={close}
            className="absolute right-4 top-4 z-10 rounded-full bg-white/10 px-3 py-2 text-sm font-medium text-white transition hover:bg-white/20"
            aria-label="Close photo"
          >
            Close
          </button>
          <button
            type="button"
            onClick={showPrev}
            className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 px-3 py-2 text-white transition hover:bg-white/20 sm:left-6"
            aria-label="Previous photo"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={showNext}
            className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 px-3 py-2 text-white transition hover:bg-white/20 sm:right-6"
            aria-label="Next photo"
          >
            ›
          </button>
          <div className="relative z-10 max-h-[90vh] max-w-[min(100%,1100px)]">
            <PhotoImg
              photo={photo}
              priority
              className="max-h-[90vh] w-auto max-w-full object-contain"
            />
            <p className="mt-3 text-center text-sm text-white/70">
              {active !== null ? `${active + 1} / ${photos.length}` : null}
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}
